import { useEffect, useRef } from 'react';

/**
 * IntersectionObserver that fires `onLoadMore` when a sentinel enters the scroll root.
 * Attach `scrollContainerRef` to the scrolling element and `loadMoreSentinelRef` to the last child.
 */
export function useInfiniteScroll<
  TContainer extends HTMLElement = HTMLDivElement,
  TSentinel extends HTMLElement = HTMLDivElement
>({
  onLoadMore,
  hasMore,
  isLoadingMore,
  itemCount
}: {
  onLoadMore?: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  /** Re-arms the observer after a page lands, in case the sentinel is still in view. */
  itemCount: number;
}) {
  const scrollContainerRef = useRef<TContainer>(null);
  const loadMoreSentinelRef = useRef<TSentinel>(null);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !onLoadMore || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root, rootMargin: '120px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore, itemCount]);

  return { scrollContainerRef, loadMoreSentinelRef };
}
